/**
 * Sales Performance stage 5: the input of `salesPerformanceAlerts`, built from the stored months, for the backend's
 * delivery to the bell. The page keeps its own copy of this (`alertSeries` in sales-performance-view.ts); a parity
 * spec in the frontend runs both on the same months and fails if they ever differ.
 *
 * Pure and side-effect free. Reach by month for the company and for every salesperson in every reportable category,
 * over COMPLETE months only (a month still running would look like a collapse), up to `monthKey`; the target is the
 * same expected reach the rows show.
 */
import { penetration } from './sales-performance';
import { AlertSeries } from './sales-performance-alerts';
import { BandCounts, BandTargets, expectedReach, fullMonths } from './sales-performance-segments';
import type { CompanyMonth, RepMonth } from './sales-performance';

export interface AlertMonth {
    key: string;
    start: string;
    end: string;
    company: CompanyMonth;
    reps: Record<string, RepMonth>;
}

export interface AlertCategory {
    key: string;
    name: string;
    excluded: boolean;
    order: number;
}

export interface AlertBands {
    byType: Record<string, BandTargets>;
    mix: Record<string, Record<string, BandCounts>>;
    companyMix: Record<string, BandCounts>;
}

/** Below this many active customers a salesperson's share swings on one sale: no alert. */
export const ALERT_FEW_CUSTOMERS = 5;
const ALERT_HISTORY = 6;

const ratio = (a: number, b: number) => (b ? a / b : 0);
const round4 = (n: number) => Math.round(n * 10000) / 10000;

function targetOf(
    cat: string,
    month: string,
    rep: string | null,
    targets: Record<string, number>,
    bands: AlertBands | undefined
): number | null {
    const mix = rep === null ? bands?.companyMix[month] : bands?.mix[month]?.[rep];
    const e = expectedReach(bands?.byType[cat], targets[cat] ?? null, mix);
    return e.target === null ? null : e.weighted ? round4(e.target) : e.target;
}

export function buildAlertSeries(
    months: AlertMonth[],
    monthKey: string,
    categories: AlertCategory[],
    names: Record<string, string>,
    targets: Record<string, number>,
    bands: AlertBands | undefined,
    lastLine: string | undefined | null
): AlertSeries[] {
    const complete = new Set(fullMonths(months, lastLine));
    const w = months
        .filter((m) => m.key <= monthKey)
        .sort((a, b) => a.key.localeCompare(b.key))
        .slice(-ALERT_HISTORY)
        .filter((m) => complete.has(m.key));
    const judged = w[w.length - 1];
    if (!judged) return [];
    const out: AlertSeries[] = [];
    const reportable = categories.filter((c) => !c.excluded).sort((a, b) => a.order - b.order);
    for (const c of reportable) {
        const point = (m: AlertMonth, value: number) => ({
            key: m.key,
            start: m.start,
            end: m.end,
            value: round4(value),
        });
        out.push({
            rep: null,
            repName: 'Company',
            category: c.key,
            categoryName: c.name,
            points: w.map((m) => point(m, ratio(m.company.categories[c.key]?.customers || 0, m.company.customers))),
            target: targetOf(c.key, judged.key, null, targets, bands),
        });
        for (const [rep, r] of Object.entries(judged.reps)) {
            if (!(r.activeCustomers > 0)) continue;
            out.push({
                rep,
                repName: names[rep] || `Rep ${rep}`,
                category: c.key,
                categoryName: c.name,
                points: w.map((m) => {
                    const mr = m.reps[rep];
                    return point(m, mr ? penetration(mr.categories[c.key]?.customers || 0, mr.activeCustomers) : 0);
                }),
                target: targetOf(c.key, judged.key, rep, targets, bands),
                fewCustomers: r.activeCustomers < ALERT_FEW_CUSTOMERS,
            });
        }
    }
    return out;
}
